import { DataPointTemplate, Visibility, WranglerItem } from "fileParsing/wrangler/types";
import { WranglerActionTypes, WranglerActions, WranglerState } from "./types";
import { Category } from "fileParsing/types";

// Initial state
const initialState: WranglerState = {
  items: [],
  counter: 0,
};

const updateItem = (items: WranglerItem[], id: number, update: Partial<WranglerItem>) => {
  const ix = items.findIndex((i) => i.id === id);
  if (ix > -1) {
    return Object.assign([], items, {
      [ix]: {
        ...items[ix],
        ...update,
      },
    });
  }

  return items;
};

const updateTemplate = (items: WranglerItem[], id: number, newTemplate: DataPointTemplate, index?: number) => {
  const itemIx = items.findIndex((i) => i.id === id);
  if (itemIx > -1) {
    const item = items[itemIx];
    const ix = item.templates.findIndex((tmpl) => tmpl.path.toString() === newTemplate.path.toString());
    if (ix > -1) {
      // Update existing template
      const templates = Object.assign([], item.templates, {
        [ix]: newTemplate,
      });
      return Object.assign([], items, {
        [itemIx]: {
          ...item,
          category: templates
            .filter(({ visibility }) => visibility !== Visibility.hidden)
            // Majority vote among templates
            .reduce<[Category, number][]>(
              (categories, { category }) => {
                const ix = categories.findIndex(([c]) => c === category);
                if (ix !== -1) {
                  return Object.assign<[Category, number][], [Category, number][], Record<number, [Category, number]>>(
                    [],
                    categories,
                    {
                      [ix]: [categories[ix][0], categories[ix][1] + 1],
                    },
                  );
                }
                return categories.concat([[category, 1]]);
              },
              [[Category.Other, 0]],
            )
            // Take category with most votes
            .sort(([, a], [, b]) => b - a)[0][0],
          templates,
        },
      });
    }
    // Add new template
    const templates = [...item.templates];
    if (index !== undefined) {
      templates.splice(index, 0, newTemplate);
    }
    return Object.assign([], items, {
      [itemIx]: {
        ...item,
        templates: index === undefined ? [...templates, newTemplate] : templates,
      },
    });
  }
  return items;
};

// The reducer function
export function wranglerReducer(state = initialState, action: WranglerActions): WranglerState {
  switch (action.type) {
    case WranglerActionTypes.ADD_DATA: {
      const { exportId, items } = action.payload;

      return {
        ...state,
        counter: state.counter + items.length,
        items: state.items.concat(
          items.map((item) => ({
            ...item,
            id: item.id + state.counter,
            exportId,
          })),
        ),
      };
    }
    case WranglerActionTypes.UPDATE_DATA: {
      const { id, template, index } = action.payload;
      return {
        ...state,
        items: updateTemplate(state.items, id, template, index),
      };
    }
    case WranglerActionTypes.UPDATE_STATUS: {
      const { id, userStatus, dataProgress: progress, compiled } = action.payload;
      const item = state.items.find((item) => item.id === id);

      if (item === undefined) return state;

      const dataProgress = { ...item.dataProgress, ...progress };
      return {
        ...state,
        items: updateItem(state.items, id, {
          userStatus,
          dataProgress,
          ...(compiled
            ? {
                templates: item.templates.map((template) => ({
                  ...template,
                  updated: false,
                })),
              }
            : {}),
        }),
      };
    }
    case WranglerActionTypes.REMOVE_EXPORT: {
      const { id } = action.payload;

      return {
        ...state,
        items: state.items.filter((item) => item.exportId !== id),
      };
    }
    default: {
      return state;
    }
  }
}
